import { ReactNode } from "react";
import { Redirect } from "wouter";
import { useCRMAuth } from "@/hooks/crm/useAuth";
import { Loader2, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

interface ProtectedCRMRouteProps {
  children: ReactNode;
}

/**
 * Protected route component for CRM section
 * Requires authentication and agent/admin role
 */
export function ProtectedCRMRoute({ children }: ProtectedCRMRouteProps) {
  const { isAuthenticated, isAgent, isLoading, logout, user } = useCRMAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Verifying authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Redirect to="/auth?redirect=/crm/dashboard" />;
  }

  // Show access denied if not an agent/admin
  if (!isAgent) {
    return (
      <div className="flex h-screen items-center justify-center bg-muted/10 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <span className="text-4xl">🚫</span>
            </div>
            <CardTitle className="text-2xl text-destructive">
              Access Denied
            </CardTitle>
            <CardDescription>
              You don't have permission to access the CRM system.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted p-4 rounded-lg">
              <p className="text-sm text-muted-foreground text-center">
                Logged in as: <span className="font-medium text-foreground">{user?.email || user?.username}</span>
              </p>
              <p className="text-xs text-muted-foreground text-center mt-1">
                This section is restricted to agents and administrators only.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  logout();
                  window.location.href = "/crm/login";
                }}
                className="w-full"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Logout & Login with Different Account
              </Button>

              <Button
                variant="ghost"
                onClick={() => window.location.href = "/"}
                className="w-full"
              >
                Return to Homepage
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // User is authenticated and authorized
  return <div className="h-full">{children}</div>;
}
